<template>
  <BlocksCard class="blocks-schedule-viewing">
    <div v-if="availableSchedules.length && !bookingCompleted" style="padding: 20px;">
      <!-- Heading -->
      <h2 class="__heading __underlined">
        <img v-lazy-load  :data-src="scheduleIcon" alt="Schedule Icon">
        Schedule a meeting
      </h2>

      <!-- Schedule form -->
      <div class="__schedule-viewing-form-wrapper">
        <ElementsInputsText v-model="name" v-model:is-valid="isValid" class="__input-field __name" :rules="{fieldName: 'your name', rules: {required: true, minLength: 3, maxLength: 30}}" placeholder="Name" />
        <ElementsInputsText v-model="phone" v-model:is-valid="isValid" class="__input-field __phone" :rules="{fieldName: 'phone', rules: {minLength: 6, maxLength: 14, phone: true}}" placeholder="Phone" />
        <ElementsInputsText v-model="email" v-model:is-valid="isValid" class="__input-field __email" placeholder="Email" :rules="{fieldName: 'email', rules: {required: true, email: true, minLength: 3, maxLength: 30}}" />
        <ElementsInputsDropdown v-model="selectedDateTime" v-model:is-valid="isValid" override-placeholder="Select the meeting time" class="__input-field __date-time" :rules="{fieldName: 'meeting time', rules: {required: true}}" :items="availableSchedules" />
      </div>

      <!-- Schedule Action -->
      <div class="__action-button">
        <ElementsButtonsRegular label="Book Now" size="md" bg-color="rgb(var(--primary))" secondary :loading="isLoading" @click="makeBooking" />
      </div>
    </div>
    <div v-else-if="bookingCompleted" style="padding: 20px;">
      <h2 class="__heading __underlined" style="color: #00b300;">
        Booking Completed
      </h2>
      <p style="margin-top: 12px;">
        Thank you for booking a meeting. Property agent will be in touch with you soon.
      </p>
    </div>
  </BlocksCard>
</template>

<script setup lang="ts">
import scheduleIcon from '~/assets/images/icons/schedule.webp';
import {Ref} from 'vue';
import {Listing} from '~/@types/listing';

// PROPS Definitions
//----------------------------------------------------------------------------------------------------------------------
const props = defineProps({
  listing: {
    type: [Object, null] as PropType<Listing>,
    required: false,
    default: null,
  },
});

// DATA
const isLoading: Ref<boolean> = ref(false);
const availableSchedules: Ref<{label: string; value: number}[]> = ref([]);
const selectedDateTime: Ref<string | null> = ref(null);
const isValid: Ref<string[]> = ref([]);
const name: Ref<string | null> = ref(null);
const phone: Ref<string | null> = ref(null);
const email: Ref<string | null> = ref(null);
const bookingCompleted: Ref<boolean> = ref(false);
const config = useRuntimeConfig();

onMounted(() => {
  getViewingSchedules();
});

async function getViewingSchedules() {
  $fetch(`/public/listings/viewing-schedule/${props.listing?.slug}`,
    { baseURL: config.public.apiUrl, headers: globalApiHeaders()},
  ).then((response) => {
    availableSchedules.value = response?.data;
  });
}

async function makeBooking () {
  // Return of the form is loading
  if (isLoading.value) {
    return;
  }

  isValid.value = ['validate'];

  nextTick(async () => {
    if (!(isValid.value.filter((item) => item !== 'validate').length)) {
      isLoading.value = true;
      const payload = {
        name: name.value,
        phone: phone.value,
        email: email.value,
        selectedDateTime: selectedDateTime.value,
      };

      setTimeout(() => {
        $fetch(`/public/listings/viewing-schedule/${props.listing?.slug}`,
          { baseURL: config.public.apiUrl, headers: globalApiHeaders(), method: 'POST', body: JSON.stringify(payload)},
        ).then((response) => {
          if (response?.code === 200) {
            bookingCompleted.value = true;
          } else {
            alert('Something went wrong. Please try again.');
          }
        });
      }, 1000);
      isLoading.value = false;
    }
  });
}
</script>

<style lang="scss">
.blocks-schedule-viewing {
  padding: 0 !important;

  .__heading {
    display: flex;
    align-items: center;

    img {
      width: 25px;
      height: auto;
      margin-right: 8px;
    }
  }

  .__schedule-viewing-form-wrapper {
    margin-top: 20px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    .__input-field {
      padding: 12px 0;

      .__input-field-wrapper {
        input {
          border-bottom: 1px solid rgb(0, 0, 0, 0.1);
        }
      }

      &.el-input-dropdown {
        padding-top: 0;

        .__input-dropdown-inner {
          position: relative;
          overflow: hidden;

          .__selection {
            color: rgb(var(--text-darken-1));
          }

          &:after {
            content: '';
            width: 190%;
            height: 1px;
            background-color: rgb(0, 0, 0, 0.1);
            position: absolute;
            bottom: 0;
          }
        }
      }
    }

    .__name {
      width: 160px;
    }

    .__phone {
      width: 160px;
    }

    .__email {
      width: 100%;
    }
  }

  .__action-button {
    margin-top: 20px;
  }
}
</style>
